import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, skipWhile } from 'rxjs/operators';
import { Agent, AgentType, allowAgents } from './apis/agent.service';
import { IPreloadSystem } from './apis/preload-system.service';
import { PRELOAD_SYSTEM, SET_PRELOAD_SYSTEM } from './utils/constants/preload';
import { allowRoles, rolesIcons, UserRoleNames } from './apis/user.service';
import { AppPath } from './utils/constants';
import { TokenUser } from './services/authentication.service';
import { SidebarItem, SidebarMenuItem } from './layouts/sidebar-menu';
import { UserRolesType } from './utils/constants/user-roles';
import { APP_TRANSLATE } from './app-translate';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  version = '1.2.20232909';

  viewImage$ = new ReplaySubject<string>();

  private agentId$ = new BehaviorSubject<number>(0);
  updateAgentId$ = new Subject<number>();

  private currentAgent$ = new BehaviorSubject<Agent>(null);
  gameType$ = new BehaviorSubject<string[]>(null);

  memberCreditFormOffcanvas$ = new Subject<MemberCreditFormData>();
  memberCreditClose$ = new Subject();
  memberClearTurn$ = new Subject<number>();

  alertCopyToClipboard$ = new BehaviorSubject<boolean>(false);

  // private PreloadSystem$ = new BehaviorSubject<IPreloadSystem>(null);

  constructor() {}

  viewImage(src: string) {
    this.viewImage$.next(src);
  }

  closeImage() {
    this.viewImage$.next(null);
  }

  // get getPreloadSystem$() {
  //   return this.PreloadSystem$.pipe(
  //     debounceTime(300),
  //     map((val) => val || 0)
  //   );
  // }

  setPreloadSystem$(data: IPreloadSystem) {
    SET_PRELOAD_SYSTEM(data);
    // this.PreloadSystem$.next(data);
  }

  get getAgentId$() {
    return this.agentId$.pipe(
      debounceTime(300),
      map((val) => val || 0)
    );
  }

  setAgentId$(data: number) {
    this.agentId$.next(data);
  }

  get getCurrentAgent$() {
    return this.currentAgent$.pipe(
      debounceTime(300),
      map((val) => val || null)
    );
  }

  setCurrentAgent$(data: Agent) {
    this.currentAgent$.next(data);
  }

  get getGameType$() {
    return this.gameType$.pipe(
      debounceTime(300),
      map((val) => val || null)
    );
  }

  setConst$(data: string[]) {
    this.gameType$.next(data);
  }

  exportCSV(data: string, name: string) {
    // var csvString = 'ı,ü,ü,ğ,ş,#Hashtag,ä,ö';
    var universalBOM = '\uFEFF';
    var a = window.document.createElement('a');
    a.setAttribute(
      'href',
      'data:text/csv; charset=utf-8,' + encodeURIComponent(universalBOM + data)
    );
    a.setAttribute('download', `${name}.csv`);
    window.document.body.appendChild(a);
    a.click();
  }

  openMemberCreditFormOffcanvas(data: MemberCreditFormData) {
    this.memberCreditFormOffcanvas$.next(data);
  }

  private sidebarMenu$ = new BehaviorSubject<SidebarItem[]>([]);
  get getSidebarMenu$() {
    return this.sidebarMenu$.pipe(
      debounceTime(300),
      skipWhile((val) => val.length === 0)
    );
  }
  setSidebarMenu$(data: SidebarItem[]) {
    this.sidebarMenu$.next(data);
  }

  mapSidebarMenu(sidebarMenu: any[]) {
    const _sidebarMenu: SidebarItem[] = JSON.parse(JSON.stringify(sidebarMenu)) || [];

    return _sidebarMenu
      .map((menu) => {
        if (menu.type == 'menu' && menu.name == 'AGENT') {
          menu.menuList = [];
          PRELOAD_SYSTEM.agent_type.forEach((type) => {
            if (type.typeId === 0) {
              // typeId = 0 is Company
              menu.menuList.push({
                link: ['', AppPath.company],
                name: 'COMPANY',
                allowRoles: ['Superadmin', 'Admin', 'Company', 'Partner'],
              });
            } else {
              const new_menu = {
                link: ['', AppPath.manageAgent, type.typeName],
                name: AgentType[type.typeName],
                allowRoles: allowRoles[type.typeName] || null,
                allowAgents: allowAgents[type.typeName] || null,
              };

              if (type.typeName == 'shareholder') {
                new_menu.allowRoles.push('Company');
              }

              menu.menuList.push(new_menu);
            }
          });
          menu.menuList.push({
            link: ['', AppPath.manageAgentSub],
            name: 'SUBUSER',
            allowRoles: ['Superadmin', 'Admin', 'Operator'],
            allowAgents: ['Shareholder', 'Senior Agent', 'Master Agent', 'Agent'],
          });
        }
        return menu;
      })
      .filter((menu) => {
        if (menu.type == 'menu' && menu.name == 'เอเย่นต์') {
          if (menu.menuList && menu.menuList.length === 0) {
            return false;
          }
        }
        return true;
      });
  }

  filterSidebarAllowed(sidebarMenu: any[], currentRoles: string[], agentType = '') {
    let _sidebarMenu = sidebarMenu
      .map((menu) => {
        if (menu.menuList && menu.menuList.length > 0) {
          menu.menuList = menu.menuList.filter((sm) => this.getAccess(sm, currentRoles, agentType));
        }
        return menu;
      })
      .filter((m) => this.getAccess(m, currentRoles, agentType))
      .filter((m) => {
        if (m.type == 'menu' && m.menuList && m.menuList.length === 0) {
          return false;
        }
        return true;
      });

    return _sidebarMenu;
  }

  spRoles: UserRolesType[] = ['Admin Accounting', 'Admin Support', 'Member Password'];
  getAccess(menu: SidebarMenuItem, currentRoles: string[], agentType = '') {
    const isSpRoles = currentRoles?.some((ur) =>
      this.spRoles.some((spR) => spR.toLowerCase() == ur?.toLowerCase())
    );
    if (isSpRoles) {
      return (
        menu.spRoles?.some((spR) =>
          currentRoles?.some((ur) => ur?.toLowerCase() == spR.toLowerCase())
        ) || false
      );
    }

    let accessRole = true;
    let accessAgent = true;

    if (menu.allowRoles) {
      accessRole = menu.allowRoles?.some((r) =>
        currentRoles?.some((ur) => ur?.toLowerCase() == r?.toLowerCase())
      );
      if (accessRole && agentType && menu.allowAgents) {
        accessAgent = menu.allowAgents?.some(
          (r) => agentType?.toLowerCase().replace('_', ' ') == r?.toLowerCase()
        );
        return accessRole && accessAgent;
      }
      return accessRole;
    }
    return true;
  }
}

export interface MemberCreditFormData {
  is_add: boolean;
  member_id: number;
}
